import React, { useEffect, useRef, useCallback } from 'react';
import { Box, Toolbar } from '@mui/material';

import PublicHeader from '../components/PublicHeader';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

// 共用 CSS + 語音頁專用 CSS
import '../components/omnichannel/omnichannel.css';
import '../components/omnichannel/voice-agent.css';

// S3 / CloudFront 資源路徑
const S3_BASE = 'https://agent-rebaz-ai.s3.ap-northeast-1.amazonaws.com';
const CDN_BASE = 'https://d3uyjtuwlqw6e9.cloudfront.net/static/landing';
const AI_AVATAR_1 = `${S3_BASE}/ai_avatar_1.png`;
const AI_AVATAR_2 = `${S3_BASE}/ai_avatar_2.png`;

// 預先計算的波形資料（來自實際音檔的 RMS 分析）
const WAVEFORMS = {
  w1:[3,100,77,76,62,29,35,85,72,61,0,33,98,46,0,63,0,3,94,77,58,41,27,48,32,30,24,0,35,48,71,27,2,7,82,76,59,60,55,86,61,54,28,36,65,0,19,67,39,30,43,37,1,83,61,0,5,98,71,37],
  w2:[12,64,63,72,91,58,63,5,1,54,86,78,65,42,4,0,37,83,75,69,65,66,63,54,42,28,87,81,65,29,0,1,70,69,21,0,2,76,77,100,49,67,57,48,59,28,3,91,84,12,0,7,83,27,0,38,96,71,49,45],
  w3:[4,87,53,70,51,27,1,66,58,30,2,75,58,49,0,55,45,0,73,46,38,34,27,53,69,4,100,63,59,56,48,61,45,51,11,36,47,55,1,41,91,51,55,56,22,26,46,0,60,67,5,75,59,0,38,0,4,83,72,36],
  w4:[3,46,67,55,59,38,73,45,60,82,89,60,0,0,50,57,24,0,0,3,3,7,90,89,96,76,70,54,56,38,94,57,57,8,2,0,0,67,1,1,78,100,39,18,0,0,2,74,35,29,85,86,40,3,90,90,61,70,49,7],
  w5:[2,2,2,63,75,66,63,68,37,49,58,50,52,50,44,41,43,17,0,0,44,1,69,0,0,2,2,58,76,45,50,32,80,39,33,0,0,32,61,42,65,32,16,0,1,4,48,55,61,73,46,31,35,100,49,42,28,42,35,25]
};
const BAR_MIN = 3;
const BAR_MAX = 22;

// 場景資料
const SCENARIOS = [
  {
    id: 1,
    theme: 'theme-blue',
    badgeClass: 'va-badge-blue',
    badge: '餐飲 \u00b7 訂位',
    title: '週末滿座高峰\nAI 自動完成訂位',
    desc: '王小姐來電訂位，AI 自動收集日期、時間、人數、姓名、電話，完成訂位確認。全程不需真人介入。',
    audio: `${CDN_BASE}/landing_voice_case1.mp3`,
    duration: '1:14',
    waveformId: 'w1',
    avatar: AI_AVATAR_1,
    avatar2: null,
    mockup: {
      icon: '✔',
      iconColor: '#166534',
      title: '訂位已確認',
      details: ['王美玲 \u00b7 0912-345-678', '3/22 週六 18:00 \u00b7 4 位'],
      tag: '已自動完成訂位',
      type: 'default',
    },
    conversation: [
      { role: 'ai', text: '您好，這裡是瑞貝思餐廳，請問需要訂位、查詢，還是其他服務呢？' },
      { role: 'customer', name: '王', text: '我想要訂這週六晚上6點，我們有4位，還有位置嗎？' },
      { role: 'ai', text: '方便告訴我，您的貴姓大名嗎？' },
      { role: 'customer', name: '王', text: '我姓王' },
      { role: 'ai', text: '王小姐您好，請稍等，馬上為您查詢。好的，目前是有位置的，麻煩您留一下聯絡電話，方便確認訂位。' },
      { role: 'customer', name: '王', text: '0912 345 678' },
      { role: 'ai', text: '跟您確認一下，訂位日期 3/22 星期六晚上 6 點，訂位人王小姐，4 位，電話 0912 345 678，請問以上資訊都正確嗎？' },
      { role: 'customer', name: '王', text: '對，沒問題' },
      { role: 'ai', text: '這邊已經幫您完成訂位，期待您的光臨。' },
    ],
  },
  {
    id: 2,
    theme: 'theme-green',
    badgeClass: 'va-badge-green',
    badge: '餐飲 \u00b7 FAQ',
    title: '客人隨口一問\nAI 秒回專業答案',
    desc: '張太太來電問有沒有素食、有沒有停車場，AI 從 FAQ 知識庫即時回答，不佔用真人時間。',
    audio: `${CDN_BASE}/landing_voice_case2.mp3`,
    duration: '0:46',
    waveformId: 'w2',
    avatar: AI_AVATAR_2,
    reverse: true,
    mockup: {
      icon: '💬',
      title: '常見問題回答',
      details: ['「你們有素食選擇嗎？」'],
      tag: 'AI 自動回覆 FAQ',
      type: 'faq',
      slots: [
        { icon: '🥬', label: '素食', status: '有提供', available: true },
        { icon: '🅿️', label: '停車場', status: '有提供', available: true },
        { icon: '🐶', label: '寵物友善', status: '不開放', available: false },
      ],
    },
    conversation: [
      { role: 'ai', text: '您好，這裡是瑞貝思餐廳，請問需要訂位、查詢，還是其他服務呢？' },
      { role: 'customer', name: '張', text: '我想請問一下，你們有提供素食選擇嗎？' },
      { role: 'ai', text: '有的，我們提供素食套餐，也可以單點素菜料理，歡迎跟服務人員說明需求。請問還有其他地方需要我為您服務的嗎？' },
      { role: 'customer', name: '張', text: '那你們有停車場嗎？' },
      { role: 'ai', text: '有的，餐廳後方有專用停車場，可停約 20 輛車，免費提供給用餐客人。' },
    ],
  },
  {
    id: 3,
    theme: 'theme-amber',
    badgeClass: 'va-badge-amber',
    badge: '餐飲 \u00b7 修改',
    title: '時間人數臨時更改\nAI 自動查找並修改訂位',
    desc: '李先生來電想改時間和人數，AI 自動查找原訂位紀錄，確認修改內容後完成更新，不需人工翻查訂位本。',
    audio: `${CDN_BASE}/landing_voice_case3.mp3`,
    duration: '1:06',
    waveformId: 'w3',
    avatar: AI_AVATAR_1,
    mockup: {
      icon: '✏️',
      title: '訂位已修改',
      details: ['李大明 \u00b7 0923-456-789'],
      tag: '已自動更新紀錄',
      type: 'modify',
      diff: [
        { label: '原', text: '3/22 週六 18:00 \u00b7 4 位', type: 'old' },
        { label: '新', text: '3/22 週六 19:30 \u00b7 6 位', type: 'new' },
      ],
    },
    conversation: [
      { role: 'ai', text: '您好，這裡是瑞貝思餐廳，請問需要訂位、查詢，還是其他服務呢？' },
      { role: 'customer', name: '李', text: '我之前有訂位，想改時間' },
      { role: 'ai', text: '好的，麻煩您提供訂位時留的電話號碼。' },
      { role: 'customer', name: '李', text: '0923 456 789' },
      { role: 'ai', text: '請稍後，馬上為您查詢。有找到您的訂位了，您目前的訂位是 3/22 星期六晚上 6 點，李先生，4 位。請問您要改日期時間、人數，還是都要改呢？' },
      { role: 'customer', name: '李', text: '改成7點半，人數改6個人' },
      { role: 'ai', text: '跟您確認，訂位要改成 3/22 晚上 7 點半，6 位，請問沒問題嗎？' },
      { role: 'customer', name: '李', text: '對，沒問題' },
      { role: 'ai', text: '已為您修改完成囉。' },
    ],
  },
  {
    id: 4,
    theme: 'theme-blue',
    badgeClass: 'va-badge-blue',
    badge: 'Outbound \u00b7 確認',
    title: '主動預約確認與提醒\n不再靠人力逐一撥打',
    desc: '餐前 1-2 天自動語音撥打，精準判斷顧客意圖（保留、取消、改期）後即時更新系統，讓團隊只處理例外狀況。',
    audio: `${CDN_BASE}/landing_voice_case4.mp3`,
    duration: '0:31',
    waveformId: 'w4',
    avatar: AI_AVATAR_2,
    reverse: true,
    mockup: {
      icon: '📤',
      title: '主動預約確認',
      tag: 'AI 批次外撥',
      type: 'outbound',
    },
    conversation: [
      { role: 'ai', text: '王先生您好，這裡是瑞貝思餐廳訂位提醒，請問現在方便說話嗎？' },
      { role: 'customer', name: '王', text: '嗯，可以' },
      { role: 'ai', text: '提醒您明天 3/22 晚上 6 點有 4 位的訂位，請問會如期出席嗎？' },
      { role: 'customer', name: '王', text: '會，但人數改成 6 個人可以嗎？' },
      { role: 'ai', text: '沒問題！已幫您更新為 6 位。明天晚上 6 點見，期待您的光臨！' },
    ],
  },
  {
    id: 5,
    theme: 'theme-green',
    badgeClass: 'va-badge-green',
    badge: 'Outbound \u00b7 關懷',
    title: '餐後滿意度調查\nAI 自動關懷回訪',
    desc: '用餐完畢後，AI 自動發起關懷電話，進行滿意度調查或邀請顧客留下評論，幫助餐廳持續優化服務品質。',
    audio: `${CDN_BASE}/landing_voice_case5.mp3`,
    duration: '0:35',
    waveformId: 'w5',
    avatar: AI_AVATAR_1,
    mockup: {
      icon: '⭐',
      title: '滿意度調查',
      tag: 'AI 自動回訪',
      type: 'survey',
    },
    conversation: [
      { role: 'ai', text: '陳小姐您好，這裡是瑞貝思餐廳，感謝您昨晚來店用餐，請問方便請教您對餐點和服務的滿意度嗎？' },
      { role: 'customer', name: '陳', text: '喔，好啊' },
      { role: 'ai', text: '想請教您對餐點和服務的滿意度，1 到 5 分您會給幾分呢？' },
      { role: 'customer', name: '陳', text: '4 分吧，餐點很好但等了比較久' },
      { role: 'ai', text: '感謝您的回饋！我們會改善出餐速度。方便的話，也歡迎在 Google 上留下評論，祝您有美好的一天！' },
    ],
  },
];

// 通話記錄表格資料
const CALL_LOG_ROWS = [
  { dir: 'in', name: '李先生', intent: '訂位', intentClass: '', dur: '1:48', time: '16:40' },
  { dir: 'in', name: '王小姐', intent: '訂位', intentClass: '', dur: '2:05', time: '14:15' },
  { dir: 'in', name: '陳先生', intent: '查詢', intentClass: 'intent-check', dur: '1:12', time: '13:40' },
  { dir: 'in', name: '林太太', intent: '取消', intentClass: 'intent-cancel', dur: '0:58', time: '12:10' },
  { dir: 'out', name: '張先生', intent: '確認', intentClass: 'intent-check', dur: '0:45', time: '10:29' },
];

const CALL_STATS = [
  { val: '14', label: '今日來電', bg: '#fff1eb', color: '#FF6B35' },
  { val: '94%', label: '完成率', bg: '#dcfce7', color: '#10b981' },
  { val: '2:15', label: '平均通話', bg: '#EBF0FF', color: '#3664D1' },
  { val: '6', label: '待回報', bg: '#fef3c7', color: '#92400e' },
];

// 電話 SVG icon
const PhoneSvg = () => (
  <svg viewBox="0 0 24 24"><path d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24 11.72 11.72 0 003.66.59 1 1 0 011 1V20a1 1 0 01-1 1A17 17 0 013 4a1 1 0 011-1h3.5a1 1 0 011 1 11.72 11.72 0 00.59 3.66 1 1 0 01-.25 1.01l-2.22 2.12z"/></svg>
);

// Mockup 卡片內容
const MockupCard = ({ scenario }) => {
  const { mockup, theme, avatar } = scenario;

  return (
    <div className={`va-mockup ${theme}`}>
      <div className="va-mockup-main">
        <div className="va-mockup-icon" style={mockup.iconColor ? { color: mockup.iconColor } : undefined}>
          {mockup.icon}
        </div>
        <div className="va-mockup-title">{mockup.title}</div>
        {mockup.details && (
          <div className="va-mockup-details">
            {mockup.details.map((d, i) => <span key={i}>{d}</span>)}
          </div>
        )}

        {/* FAQ slots */}
        {mockup.type === 'faq' && mockup.slots && (
          <div className="va-mockup-slots">
            {mockup.slots.map((s, i) => (
              <div key={i} className="va-mockup-slot">
                <span className="slot-time">{s.icon} {s.label}</span>
                <span className={`slot-status ${s.available ? 'available' : 'full'}`}>{s.status}</span>
              </div>
            ))}
          </div>
        )}

        {/* Modify diff */}
        {mockup.type === 'modify' && mockup.diff && (
          <div className="va-mockup-diff">
            {mockup.diff.map((d, i) => (
              <div key={i} className={`va-mockup-diff-row ${d.type}`}>
                <span className="diff-label">{d.label}</span>{d.text}
              </div>
            ))}
          </div>
        )}

        {/* Outbound flow */}
        {mockup.type === 'outbound' && (
          <div className="va-mockup-flow">
            <div className="va-flow-root">致電顧客</div>
            <div className="va-flow-line" />
            <div className="va-flow-branches">
              <div className="va-flow-branch confirm">確認出席<br/>→ 更新系統</div>
              <div className="va-flow-branch cancel">取消訂位<br/>→ 釋出桌位</div>
            </div>
          </div>
        )}

        {/* Survey rating */}
        {mockup.type === 'survey' && (
          <>
            <div className="va-mockup-rating">
              {[1,2,3,4].map(i => <span key={i} className="star">⭐</span>)}
              <span className="star dim">⭐</span>
            </div>
            <div className="va-mockup-stats">
              <div className="va-mockup-stat"><span className="stat-val">92%</span><span className="stat-label">滿意率</span></div>
              <div className="va-mockup-stat"><span className="stat-val">48</span><span className="stat-label">今日完成</span></div>
              <div className="va-mockup-stat"><span className="stat-val">4.2</span><span className="stat-label">平均分</span></div>
            </div>
          </>
        )}

        <div className="va-mockup-tag">{mockup.tag}</div>
      </div>

      {/* AI avatar */}
      <div className="va-mockup-avatar">
        <img src={avatar} alt="AI" />
      </div>

      {/* Phone icon */}
      <div className="va-prop">
        <PhoneSvg />
      </div>
    </div>
  );
};

// 音訊播放器元件
const AudioPlayer = ({ scenario, audioRef, currentPlayingId, onPlay }) => {
  const wavesRef = useRef(null);

  // 建立波形條
  useEffect(() => {
    const container = wavesRef.current;
    if (!container || container.children.length > 0) return;

    const data = WAVEFORMS[scenario.waveformId];
    if (!data) return;

    data.forEach(v => {
      const bar = document.createElement('div');
      bar.className = 'va-wave-bar';
      bar.style.height = (BAR_MIN + (BAR_MAX - BAR_MIN) * v / 100) + 'px';
      container.appendChild(bar);
    });
  }, [scenario.waveformId]);

  const isPlaying = currentPlayingId === scenario.id;

  return (
    <div
      className={`va-audio${isPlaying ? ' playing' : ''}`}
      onClick={() => onPlay(scenario.id, scenario.audio, wavesRef.current)}
    >
      <div className="va-audio-play">
        <svg className="play-icon" viewBox="0 0 24 24"><polygon points="8,5 20,12 8,19"/></svg>
        <svg className="pause-icon" viewBox="0 0 24 24"><rect x="6" y="5" width="4" height="14"/><rect x="14" y="5" width="4" height="14"/></svg>
      </div>
      <div className="va-audio-waves" ref={wavesRef} />
      <div className="va-audio-time" data-scenario-id={scenario.id}>{scenario.duration}</div>
      <div className="va-audio-cta" data-scenario-id={scenario.id}>
        {isPlaying ? '暫停播放' : '播放電話錄音'}
      </div>
    </div>
  );
};

// 對話氣泡元件
const Conversation = ({ messages }) => (
  <div className="va-conv">
    {messages.map((msg, i) => (
      <div key={i} className={`va-msg ${msg.role}`}>
        <div className="va-ava">
          {msg.role === 'ai' ? (
            <img src={AI_AVATAR_1} alt="AI" />
          ) : (
            msg.name
          )}
        </div>
        <div className="va-bubble">{msg.text}</div>
      </div>
    ))}
  </div>
);

// 場景列元件
const ScenarioRow = ({ scenario, audioRef, currentPlayingId, onPlay }) => {
  const lines = scenario.title.split('\n');

  return (
    <div className={`va-row omni-reveal${scenario.reverse ? ' reverse' : ''}`}>
      <div className="va-image">
        <MockupCard scenario={scenario} />
      </div>
      <div className="va-content">
        <div className={`va-badge ${scenario.badgeClass}`}>{scenario.badge}</div>
        <h4>{lines.map((line, i) => <React.Fragment key={i}>{line}{i < lines.length - 1 && <br/>}</React.Fragment>)}</h4>
        <p className="va-sc-desc">{scenario.desc}</p>
        <AudioPlayer
          scenario={scenario}
          audioRef={audioRef}
          currentPlayingId={currentPlayingId}
          onPlay={onPlay}
        />
        <Conversation messages={scenario.conversation} />
      </div>
    </div>
  );
};

const VoiceAgentPage = () => {
  const audioObjRef = useRef(null);
  const currentPlayerRef = useRef(null);
  const [currentPlayingId, setCurrentPlayingId] = React.useState(null);

  // Scroll-reveal observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );
    document.querySelectorAll('.omni-reveal').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  // 音訊播放處理
  const handlePlay = useCallback((scenarioId, audioSrc, wavesContainer) => {
    // 如果點擊同一個正在播放的播放器
    if (audioObjRef.current && currentPlayerRef.current?.scenarioId === scenarioId) {
      if (audioObjRef.current.paused) {
        audioObjRef.current.play();
        setCurrentPlayingId(scenarioId);
      } else {
        audioObjRef.current.pause();
        setCurrentPlayingId(null);
      }
      return;
    }

    // 停止其他播放
    if (audioObjRef.current) {
      audioObjRef.current.pause();
      audioObjRef.current.currentTime = 0;
      // 清除前一個的波形
      if (currentPlayerRef.current?.waves) {
        currentPlayerRef.current.waves.querySelectorAll('.va-wave-bar').forEach(b => b.classList.remove('active'));
      }
    }

    // 建立新音訊
    const audio = new Audio(audioSrc);
    audioObjRef.current = audio;
    currentPlayerRef.current = { scenarioId, waves: wavesContainer };
    setCurrentPlayingId(scenarioId);

    const bars = wavesContainer?.querySelectorAll('.va-wave-bar') || [];
    const totalBars = bars.length;

    audio.addEventListener('timeupdate', () => {
      const progress = audio.currentTime / (audio.duration || 1);
      const activeBars = Math.floor(progress * totalBars);
      bars.forEach((bar, i) => {
        bar.classList.toggle('active', i < activeBars);
      });

      // 更新時間顯示
      const timeEl = document.querySelector(`.va-audio-time[data-scenario-id="${scenarioId}"]`);
      if (timeEl) {
        const m = Math.floor(audio.currentTime / 60);
        const s = Math.floor(audio.currentTime % 60).toString().padStart(2, '0');
        timeEl.textContent = m + ':' + s;
      }
    });

    audio.addEventListener('ended', () => {
      setCurrentPlayingId(null);
      bars.forEach(bar => bar.classList.remove('active'));
      // 恢復原始時間
      const scenario = SCENARIOS.find(s => s.id === scenarioId);
      const timeEl = document.querySelector(`.va-audio-time[data-scenario-id="${scenarioId}"]`);
      if (timeEl && scenario) {
        timeEl.textContent = scenario.duration;
      }
      audioObjRef.current = null;
      currentPlayerRef.current = null;
    });

    audio.play();
  }, []);

  // 離開頁面時停止播放
  useEffect(() => {
    return () => {
      if (audioObjRef.current) {
        audioObjRef.current.pause();
        audioObjRef.current = null;
      }
    };
  }, []);

  const voiceAgentSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Rebaz AI Voice Agent",
    "applicationCategory": "BusinessApplication",
    "applicationSubCategory": "Voice AI",
    "operatingSystem": "Web",
    "url": "https://rebaz.ai/voice-agent",
    "description": "AI 自動接聽來電，像真人一樣完成訂位、查詢、修改。通話結束自動產生摘要，主管即時掌握每通電話的完整資訊。",
    "featureList": [
      "AI 自動接聽，辨識訂位、查詢、取消意圖",
      "通話結束自動產生摘要與客戶資料",
      "複雜問題一鍵轉接真人，對話無縫銜接",
      "主動外撥確認與滿意度回訪"
    ],
    "offers": {
      "@type": "Offer",
      "priceCurrency": "TWD",
      "availability": "https://schema.org/InStock",
      "url": "https://rebaz.ai/pricing"
    }
  };

  const voiceAgentBreadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "首頁", "item": "https://rebaz.ai" },
      { "@type": "ListItem", "position": 2, "name": "AI 語音客服", "item": "https://rebaz.ai/voice-agent" }
    ]
  };

  return (
    <>
      <SEO
        title="餐廳 AI 語音客服 — AI 電話訂位自動接聽"
        description="餐廳 AI 語音客服，AI 自動接聽訂位電話，像真人一樣完成訂位、查詢、修改。通話結束自動產生摘要，24/7 不漏接每通來電。"
        keywords="餐廳 AI 語音, 餐廳 AI 語音客服, AI 電話訂位, AI 語音訂位, AI 自動接聽電話, 餐廳訂位系統, AI 語音客服, 語音助理, 餐飲業 AI, Voice Agent"
        canonicalUrl="https://rebaz.ai/voice-agent"
        schema={voiceAgentSchema}
        breadcrumb={voiceAgentBreadcrumb}
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <PublicHeader />
        <Toolbar />

        <Box component="main" sx={{ flexGrow: 1 }}>
          {/* Section 1: FeatureCallLog 樣式的介紹區 */}
          <section className="va-section">
            <div className="va-inner">
              <div className="va-grid">
                {/* 文字 — 左側 */}
                <div className="omni-reveal">
                  <div className="va-label">
                    <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>
                    AI VOICE AGENT
                  </div>
                  <h1 className="va-title">餐廳 AI 語音客服<br/>AI 電話訂位 24/7 不漏接</h1>
                  <p className="va-desc">AI 自動接聽來電，像真人一樣完成訂位、查詢。通話結束自動產生摘要，主管即時掌握每通電話的完整資訊。</p>
                  <div className="va-bullet"><div className="va-bullet-check">&#10003;</div><span>AI 自動接聽，辨識訂位、查詢、取消意圖</span></div>
                  <div className="va-bullet"><div className="va-bullet-check">&#10003;</div><span>通話結束自動產生摘要與客戶資料</span></div>
                  <div className="va-bullet"><div className="va-bullet-check">&#10003;</div><span>複雜問題一鍵轉接真人，對話無縫銜接</span></div>
                </div>

                {/* Mockup — 右側 */}
                <div className="omni-feature-visual omni-reveal omni-reveal-delay-1">
                  <div className="omni-mockup-frame">
                    <div className="omni-mockup-chrome">
                      <div className="omni-mockup-dots"><span /><span /><span /></div>
                      <div className="omni-mockup-title">Rebaz AI — 通話紀錄</div>
                    </div>
                    <div>
                      {/* 統計卡片 */}
                      <div className="mock-call-stats">
                        {CALL_STATS.map((stat, i) => (
                          <div key={i} className="mock-call-stat" style={{ background: stat.bg }}>
                            <div className="s-val" style={{ color: stat.color }}>{stat.val}</div>
                            <div className="s-label">{stat.label}</div>
                          </div>
                        ))}
                      </div>

                      {/* 通話記錄表格 */}
                      <table className="mock-call-table">
                        <thead>
                          <tr>
                            <th>方向</th><th>來電者</th><th>意圖</th><th>時長</th><th>時間</th>
                          </tr>
                        </thead>
                        <tbody>
                          {CALL_LOG_ROWS.map((row, i) => {
                            const isIn = row.dir === 'in';
                            return (
                              <tr key={i}>
                                <td>
                                  <div className="mock-call-dir" style={{
                                    background: isIn ? '#dcfce7' : '#dbeafe',
                                    color: isIn ? '#166534' : '#1e40af'
                                  }}>
                                    {isIn ? '↙ 進線' : '↗ 外撥'}
                                  </div>
                                </td>
                                <td style={{ fontWeight: 600, color: '#0f1729' }}>{row.name}</td>
                                <td><div className={`mock-call-intent ${row.intentClass}`}>{row.intent}</div></td>
                                <td>{row.dur}</td>
                                <td style={{ color: '#6b7280' }}>{row.time}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 2: Zig-zag 場景展示 */}
          <section className="va-scenarios">
            <div className="va-inner">
              <div className="va-scenarios-headline omni-reveal">
                <h3>真實場景，AI 從容應對</h3>
                <p>來自實際客戶案例，聽聽 AI 如何處理不同產業的來電</p>
              </div>

              {SCENARIOS.map((scenario) => (
                <ScenarioRow
                  key={scenario.id}
                  scenario={scenario}
                  audioRef={audioObjRef}
                  currentPlayingId={currentPlayingId}
                  onPlay={handlePlay}
                />
              ))}

              <p style={{ textAlign: 'center', fontSize: '13px', color: '#9ca3af', marginTop: '48px', position: 'relative', zIndex: 1 }}>
                ＊ 以上對話為參考示意，實際對話內容依業主需求設定為主
              </p>
            </div>
          </section>

          {/* Section 3: 三步驟部署 */}
          <section className="va-steps">
            <div className="va-inner">
              <div className="va-steps-headline omni-reveal">
                <h3>快速部署，立即上線</h3>
                <p>不需複雜整合，三步驟讓 AI 開始為您接聽電話</p>
              </div>
              <div className="va-steps-grid">
                <div className="va-step omni-reveal">
                  <div className="va-step-num">1</div>
                  <div className="va-step-title">選擇語音腳本</div>
                  <div className="va-step-desc">從產業範本庫選擇適合的對話腳本，餐廳訂位、預約諮詢，一鍵啟用</div>
                </div>
                <div className="va-step omni-reveal omni-reveal-delay-1">
                  <div className="va-step-num">2</div>
                  <div className="va-step-title">調整對話內容</div>
                  <div className="va-step-desc">用視覺化編輯器修改 AI 對話文字，營業時間、服務項目、價格，不需技術背景</div>
                </div>
                <div className="va-step omni-reveal omni-reveal-delay-2">
                  <div className="va-step-num">3</div>
                  <div className="va-step-title">上線接聽</div>
                  <div className="va-step-desc">設定完成，AI 立即開始接聽，通話紀錄自動同步到後台，隨時掌握每通電話</div>
                </div>
              </div>
            </div>
          </section>
        </Box>

        <Footer />
      </Box>
    </>
  );
};

export default VoiceAgentPage;
